import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const NearFarGame: React.FC = () => {
    const emojis = [
        "🐶", "🚗", "🦄", "🎈", "🚀", "🐘", "🍎", "🏀", "📕", "⚽",
        "🍕", "🎸", "🚢", "🐱", "🌞", "🍔", "🚲", "🏕", "📡", "🔑",
        "🐳", "🌵", "🎠", "🍩", "🧀", "🚂", "🐙", "🦆", "🏖", "📖",
        "🍉", "🌊", "🎻", "🌈", "🍟", "⚡", "🎯", "🐟", "🚜", "🦅",
        "📱", "📚", "🥕", "🦖", "🎃", "🐬", "🎁", "🍇", "🚀", "🛶",
        "🦜", "🏹", "🐧", "🦊", "🏰", "🚦", "🎠", "🌼", "💎", "🗻",
        "🦄", "📌", "🥦", "🎷", "🔬", "🐼", "📎", "🦕", "🍵", "⚔️",
        "🦉", "🌙", "🎭", "🏜", "📀", "🦢", "🐎", "🧊", "🎇", "🏄",
        "🚁", "🦋", "🦔", "🍪", "🛰", "🐝", "🎤", "🦑", "🏎", "🍍"
    ];

    const gridSize = 5; // 5x5 Grid
    const [grid, setGrid] = useState<string[][]>([]);
    const [nearEmoji, setNearEmoji] = useState<string>("");
    const [farEmoji, setFarEmoji] = useState<string>("");
    const [questionType, setQuestionType] = useState<"near" | "far">("near");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Near and Far Worksheets for UKG | Spatial Concept Activities";
    }, []);

    const generateQuestion = () => {
        const emoji1 = emojis[Math.floor(Math.random() * emojis.length)];
        let emoji2;
        do {
            emoji2 = emojis[Math.floor(Math.random() * emojis.length)];
        } while (emoji1 === emoji2);

        setNearEmoji(emoji1);
        setFarEmoji(emoji2);
        setMessage("");

        // Randomly decide if the question is "near" or "far"
        setQuestionType(Math.random() < 0.5 ? "near" : "far");

        // Create empty grid
        const newGrid = Array(gridSize)
            .fill(null)
            .map(() => Array(gridSize).fill(""));

        const houseRow = Math.floor(gridSize / 2);
        const houseCol = Math.floor(gridSize / 2);
        newGrid[houseRow][houseCol] = "🏠";

        // Get valid near positions (adjacent to house)
        const nearPositions = [
            [houseRow - 1, houseCol],
            [houseRow + 1, houseCol],
            [houseRow, houseCol - 1],
            [houseRow, houseCol + 1]
        ].filter(([r, c]) => r >= 0 && r < gridSize && c >= 0 && c < gridSize);

        // Pick a random near position
        const [nearRow, nearCol] = nearPositions[Math.floor(Math.random() * nearPositions.length)];
        newGrid[nearRow][nearCol] = emoji1;

        // Get valid far positions (not adjacent to house)
        const farPositions = [];
        for (let r = 0; r < gridSize; r++) {
            for (let c = 0; c < gridSize; c++) {
                if (newGrid[r][c] === "" && Math.abs(r - houseRow) + Math.abs(c - houseCol) > 2) {
                    farPositions.push([r, c]);
                }
            }
        }

        const [farRow, farCol] = farPositions[Math.floor(Math.random() * farPositions.length)];
        newGrid[farRow][farCol] = emoji2;

        setGrid(newGrid);
    };

    const checkAnswer = (selected: string) => {
        if ((questionType === "near" && selected === nearEmoji) ||
            (questionType === "far" && selected === farEmoji)) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (

        <>
            <Helmet>
                <meta name="description" content="Help UKG kids learn spatial positions with near and far activities. Download free printable worksheets to build observation and comparison skills in early learners." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/near-far" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Near and Far</h1>

                                    <p>
                                        The concepts of <strong>near</strong> and <strong>far</strong> help children understand distance and positioning. These spatial awareness skills are a part of <strong>pre-math learning</strong> and are essential for everyday observation and understanding surroundings. Mama’s Guidance provides <strong>near and far worksheets for UKG</strong> children that make learning these ideas fun and visual.
                                    </p>

                                    <h2>📍 What Are Near and Far?</h2>
                                    <p>
                                        <strong>Near</strong> means close to you, while <strong>far</strong> means away from you. Teaching this helps children compare distances and understand location and perspective.
                                    </p>

                                    <ul>
                                        <li>The cat sitting next to the boy is <strong>near</strong>.</li>
                                        <li>The mountain in the background is <strong>far</strong>.</li>
                                        <li>The ball near the box and the ball far from it show contrast in distance.</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify objects that are near and far in pictures</li>
                                        <li>Understand perspective and spatial distance</li>
                                        <li>Build observation and comparison skills</li>
                                        <li>Use position words confidently</li>
                                    </ul>

                                    <h2>🖍️ What’s Inside the Worksheets?</h2>
                                    <ul>
                                        <li>Circle the object that is near or far</li>
                                        <li>Match near and far images with correct words</li>
                                        <li>Color the object that is near in a group of objects</li>
                                        <li>Sorting and cut-and-paste activities</li>
                                        <li>Fill-in-the-blank exercises using near/far vocabulary</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents and Educators</h2>
                                    <ul>
                                        <li>Play <strong>“Spot the near object”</strong> game around the house</li>
                                        <li>Use toy cars or animals and place them at different distances</li>
                                        <li>Read picture books that show near and far landscapes</li>
                                        <li>Ask children to describe what they see as near or far in outdoor scenes</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Learning spatial words like near and far helps kids describe the world around them better. Use daily moments — walks, playing, or storytelling — to reinforce these concepts naturally.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Near and Far</h1>

                                <p>
                                    The concepts of <strong>near</strong> and <strong>far</strong> help children understand distance and positioning. These spatial awareness skills are a part of <strong>pre-math learning</strong> and are essential for everyday observation and understanding surroundings. Mama’s Guidance provides <strong>near and far worksheets for UKG</strong> children that make learning these ideas fun and visual.
                                </p>

                                <h2>📍 What Are Near and Far?</h2>
                                <p>
                                    <strong>Near</strong> means close to you, while <strong>far</strong> means away from you. Teaching this helps children compare distances and understand location and perspective.
                                </p>

                                <ul>
                                    <li>The cat sitting next to the boy is <strong>near</strong>.</li>
                                    <li>The mountain in the background is <strong>far</strong>.</li>
                                    <li>The ball near the box and the ball far from it show contrast in distance.</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify objects that are near and far in pictures</li>
                                    <li>Understand perspective and spatial distance</li>
                                    <li>Build observation and comparison skills</li>
                                    <li>Use position words confidently</li>
                                </ul>

                                <h2>🖍️ What’s Inside the Worksheets?</h2>
                                <ul>
                                    <li>Circle the object that is near or far</li>
                                    <li>Match near and far images with correct words</li>
                                    <li>Color the object that is near in a group of objects</li>
                                    <li>Sorting and cut-and-paste activities</li>
                                    <li>Fill-in-the-blank exercises using near/far vocabulary</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents and Educators</h2>
                                <ul>
                                    <li>Play <strong>“Spot the near object”</strong> game around the house</li>
                                    <li>Use toy cars or animals and place them at different distances</li>
                                    <li>Read picture books that show near and far landscapes</li>
                                    <li>Ask children to describe what they see as near or far in outdoor scenes</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Learning spatial words like near and far helps kids describe the world around them better. Use daily moments — walks, playing, or storytelling — to reinforce these concepts naturally.
                                </p>
                            </article>
                            <h2>
                                {questionType === "near"
                                    ? "Which object is NEAR the house? 🏠"
                                    : "Which object is FAR from the house? 🏠"}
                            </h2>
                            <div className="">
                                {grid.map((row, rowIndex) => (
                                    <div key={rowIndex} className="grid-row">
                                        {row.map((cell, colIndex) => (
                                            <div
                                                key={colIndex}
                                                className={`grid-cell ${cell === "" ? "disabled" : ""}`}
                                                onClick={() => cell !== "" && cell !== "🏠" && checkAnswer(cell)}
                                            >
                                                {cell}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default NearFarGame;
